import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout,
} from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as xtpXdpConnectionSelectors from 'data/xtp-xdp-connection/xtp-xdp-connection.selectors';
import * as xtpXdpConnectionActions from 'data/xtp-xdp-connection/xtp-xdp-connection.actions';
import * as xdpActions from 'data/xdp/xdp.actions';
import * as xtpActions from 'data/xtp/xtp.actions';
import * as xdpSelectors from 'data/xdp/xdp.selectors';
import * as xtpSelectors from 'data/xtp/xtp.selectors';
import { XtpXdpConnection, newXtp } from 'data/xtp-xdp-connection/xtp-xdp-connection.types';
import { FormTemplate } from 'data/template/template.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import XtpXdpConnectionForm from '../../components/XtpXdpConnectionForm';
import { Ref } from '../../components/XtpXdpConnectionForm/XtpXdpConnectionForm';
import { xtpXdpConnectionTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptFormData,
  adaptXtpXdpConnectionTemplate,
} from '../../utils/xtpXdpConnectionTemplate.adapter';

type RouterParams = {
  history: string;
};
const mapState = (state: RootState) => ({
  xdpTemplateDef: adaptTemplateVisibilityMode(
    xtpXdpConnectionTemplate,
    'view',
  ),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  xdps: xdpSelectors.getXdps(state),
  xtps: xtpSelectors.getXtps(state),
  xtpXdpConnection: xtpXdpConnectionSelectors.getXtpXdpsConnection(state),
  selectedXtpXdpConnection: xtpXdpConnectionSelectors.getSelectedXtpXdpsConnection(state)
});

const mapDispatch = {
  goToXtpXdpConnectionEdit: xtpXdpConnectionActions.goToXtpXdpConnectionEdit,
  goToXdpList: xtpXdpConnectionActions.goToXtpXdpConnectionList,
  getXtpXdpConnectionById: xtpXdpConnectionActions.getXtpXdpConnectionById,
  getXdps: xdpActions.getXdps,
  getXtps: xtpActions.getXtps,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const XtpXdpConnectionView = ({
  history,
  userId,
  deliveryFunctionId,
  userName,
  isAuditEnabled,
  xdpTemplateDef,
  xdps,
  xtps,
  xtpXdpConnection,
  selectedXtpXdpConnection,
  goToXtpXdpConnectionEdit,
  goToXdpList,
  getXtps,
  getXdps,
  getXtpXdpConnectionById,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<XtpXdpConnection>(
    cloneDeep(newXtp),
  );
  const [template, setTemplate] = useState(xdpTemplateDef);
  const { t } = useTranslation();

  useEffect(() => {
    getXdps(deliveryFunctionId);
    getXtps(deliveryFunctionId);
    const id = window.btoa(selectedXtpXdpConnection?.[0]?.cfid);

    if (id) {
      getXtpXdpConnectionById(deliveryFunctionId, id);
    }
  }, [
    deliveryFunctionId,
    selectedXtpXdpConnection,
    getXtpXdpConnectionById,
    getXdps,
    getXtps
  ]);

  useEffect(() => {
    if (!xtpXdpConnection?.id) {
      return;
    }

    setFormData({
      ...xtpXdpConnection
    });

    const newTemplateData = adaptXtpXdpConnectionTemplate(
      xdpTemplateDef as FormTemplate,
      xdps,
      xtps,
      xtpXdpConnection,
    );

    newTemplateData.key = Date.now().toString()
    newTemplateData.formId = Date.now().toString()

    setTemplate(newTemplateData);

  }, [xdpTemplateDef, xdps, xtps, xtpXdpConnection]);

  const handleCancel = () => goToXdpList(history);

  const handleEdit = () => goToXtpXdpConnectionEdit(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <XtpXdpConnectionForm
          ref={formRef}
          data={formData}
          template={template}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickEdit={handleEdit}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (xtpXdpConnection == null) {
    return <div />;
  }

  return (
    <div data-testid="XtpXdpConnectionView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('xdp.view.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>{renderFormPanel('')}</FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(XtpXdpConnectionView));
